import React, {useEffect, Suspense, lazy} from 'react';
import {Route, Routes, useNavigate, Outlet} from 'react-router-dom';
import {useDispatch, useSelector} from "react-redux";
import {MainLoader, removeMainLoader} from "@netapp/bxp-design-system-react";
import MainErrorBoundary from "modules/app/components/MainErrorBoundary";
import AppNotifications from "modules/app/components/AppNotifications";
import {RootState} from "./store";
import MainHeader from "modules/app/components/MainHeader"
import NewServiceNav from "modules/app/components/NewServiceNav.tsx";
import {css} from '@emotion/react';
import ServiceIntegration from "modules/integrations/ServiceIntegration"
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import _, {isEmpty} from "lodash";
import {postReadyMassage, setConsoleFrontEndDomain, SHELL_HOST_HEADER_HEIGHT} from "ui-utils/integrations"
import {CloudShellSlider} from "modules/console/CloudShellSlider";
import {useConsole} from "modules/console/consoleStore";
import useCurrentService from "modules/integrations/useCurrentService.ts";

const baseCss = css`
  display: grid;
  grid-template-rows: 72px 1fr;

  grid-template-columns: 1fr;
  height: 100vh;
`;

const containerCss = css`
  overflow: hidden;
  background-color: var(--background-main);
`;

const miniContainerCss = css`
  background-color: var(--background-main);
`;

const mainPaneCss = css`
  position: relative;
  height: 100%;
`;

const outletCss = css`
  overflow-y: auto;
`;



const Homepage = lazy(() => import('./modules/homepage')/* webpackChunkName: "Homepage"*/)
const FirstTimeUserPane = lazy(() => import('./modules/app/components/FirstTimeUserPane')/* webpackChunkName: "FirstTimeUserPane"*/)
const NetAppInternal = lazy(() => import('./modules/netapp-internal')/* webpackChunkName: "NetappInternal"*/)
const ServiceAccounts = lazy(() => import('./modules/service-accounts')/* webpackChunkName: "ServiceAccounts"*/)
const K8sLearnMore = lazy(() => import('modules/markting/K8sLearnMore.tsx')/* webpackChunkName: "K8eLearnMore"*/)
const Administration = lazy(() => import('modules/administration')/* webpackChunkName: "Administration"*/)

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 0,
            refetchOnWindowFocus: false,
            retryOnMount: false,
            staleTime: 120 * 1000
        },
    },
});

const serviceIntegrationCss = css`
    display: grid;
    grid-template-columns: auto 1fr;
`

const ServiceOutlet = () => {
    const currentService = useCurrentService();
    return (currentService && !isEmpty(currentService.serviceNav?.tabs)) ? <div css={serviceIntegrationCss}>
        <NewServiceNav serviceConfig={currentService}/>
        <div css={outletCss}>
            <Outlet/>
        </div>
    </div> : <Outlet/>
}

const MainPane = () => {
    const serviceIntegrationPaths = useSelector((state: RootState) => state.services.serviceIntegrationPaths);
    const isWorkloadsLoaded = useSelector((state: RootState) => state.services.isWorkloadsLoaded);
    const {setIsConsoleOpen, isConsoleOpen, consoleHeight, addTerminalWithCredentials} = useConsole((state) => state);
    useEffect(() => {
        const handleCustomEvent = (event) => {
            setIsConsoleOpen(true);
            addTerminalWithCredentials(event.detail);
        };
        document.addEventListener('cloudshell-new-terminal', handleCustomEvent);
        return () => {
            document.removeEventListener('cloudshell-new-terminal', handleCustomEvent);
        };
    }, []);

    if (!isWorkloadsLoaded) {
        return <MainLoader/>
    }

    return <div css={mainPaneCss}>
        <div
            style={{
                display: 'grid',
                gridTemplateRows: `calc(100% - ${isConsoleOpen ? consoleHeight : '0px'}) ${isConsoleOpen ? consoleHeight : '0px'}`,
                gridTemplateColumns: '100%',
                height: '100%',
                maxHeight:'100%'
            }}>
            <MainErrorBoundary>
                <Suspense fallback={<MainLoader/>}>
                    <Routes>
                        <Route element={<ServiceOutlet/>}>
                            <Route path="/netapp-internal/*" element={<NetAppInternal/>}/>
                            <Route path="/service-accounts/*" element={<ServiceAccounts/>}/>
                            <Route path="/k8s/*" element={<K8sLearnMore/>}/>
                            <Route path="/administration/*" element={<Administration/>}/>
                            <Route path="*" element={<Homepage/>}/>
                            {serviceIntegrationPaths.map(path => {
                                return <Route key="path" path={path + "/*"} element={<ServiceIntegration/>}/>
                            })}
                        </Route>
                    </Routes>
                </Suspense>
            </MainErrorBoundary>
            {isConsoleOpen && <CloudShellSlider/>}
        </div>
    </div>
}

const MiniMainPane = (props?: any) => {
    const isWorkloadsLoaded = useSelector((state: RootState) => state.services.isWorkloadsLoaded);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isIntegratedMFE = import.meta.env.VITE_IS_APP_INTEGRATED_MFE;
	if (isIntegratedMFE) {
		dispatch({
			type: "AUTH/MINI-BOOT",
			payload: props
		})
		dispatch({
			type: "FEATURES/MINI-INITIALIZE",
			payload: props.features
		})
	}
    useEffect(() => {
            const listener = (event: any) => {
                if (event.data.type === 'SERVICE:ON-READY') {
                    // TODO This can be removed once we fully switch to shadow dom (including administration tab)
                    const {payload} = event.data;
                    setConsoleFrontEndDomain(payload.domain);
                    dispatch({
                        type: "AUTH/MINI-BOOT",
                        payload
                    })
                    dispatch({
                        type: "FEATURES/MINI-INITIALIZE",
                        payload: payload.features
                    })
                    navigate((payload.pathname?.replace("/fsxhome","").replace("/fsxadministration","/administration" )|| "") + (payload.search ? `${payload.search}` : '') + (payload.hash ? `${payload.hash}` : ''), payload.shellContext ? { state: payload.shellContext }: undefined )
                } else if (event.data.type === 'SERVICE:LOCATION-CHANGE') {
                    const {payload} = event.data;
                    const url = payload.pathname?.replace("/fsxadministration","/administration") + (payload.search ? `${payload.search}` : '') + (payload.hash ? `${payload.hash}` : '')
                    dispatch({
                        type: "APP/WORKLOADS/LOCATION-CHANGE",
                        payload: {
                            pathname: `${payload.pathname}${payload.search}`,
                        }
                    })
                    navigate(url);
                } else if (event.data.type === 'SERVICE:FEATURES-CHANGE') {
                    const {payload} = event.data;
                    dispatch({
                        type: "FEATURES/UPDATE",
                        payload: payload.features
                    })
                } else if (event.data.type === 'SERVICE:CONNECTOR-CHANGE') {
                    const {payload} = event.data;
                    dispatch({
                        type: "AUTH/CONNECTOR-UPDATE",
                        payload: { connectorId: payload.connectorId, connectorName: payload.connectorName}
                    })
                } else if (event.data.type === 'SERVICE:TOKEN-UPDATE') {
                    const {payload} = event.data;
                    dispatch({
                        type: "AUTH/TOKEN-UPDATE",
                        payload: { userMetadata: payload.userMetadata, accessToken: payload.accessToken}
                    })
                }
            };

            window.addEventListener('message', listener, false);

            postReadyMassage();

            return () => {
                window.removeEventListener("message", listener);
            }
    }, [])

    if (!isWorkloadsLoaded) {
        return <MainLoader/>
    }


    return <div style={{height: `calc(100vh - ${SHELL_HOST_HEADER_HEIGHT}px)`, overflowY: 'hidden'}}>
        <MainErrorBoundary>
            <Suspense fallback={<MainLoader/>}>
                <Routes>
                    <Route path="/administration/*" element={<Administration/>}/>
                    <Route path="/fsxhome/k8s/*" element={<K8sLearnMore/>}/>
                    <Route path="*" element={<Homepage/>}/>
                </Routes>
            </Suspense>
        </MainErrorBoundary>
    </div>
}



const MainApp = (props: any) => {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const jobDescription = useSelector((state: RootState) => state.user.jobDescription);
    const isJobDescriptionLoaded = useSelector((state: RootState) => state.user.isJobDescriptionLoaded);
    const userId = useSelector((state: RootState) => state.auth.userId);
    const accountStatus = useSelector((state: RootState) => state.tenancy.accountStatus);
    const selectedAccountId = useSelector((state: RootState) => state.tenancy.selectedAccountId);
    const configs = useSelector((state: RootState) => state.services.workloadConfigs)
    const isIntegratedMFE = import.meta.env.VITE_IS_APP_INTEGRATED_MFE;
    const isIframe = window.top !== window;
    const loadedUnderBXP = isIntegratedMFE || isIframe; // remove iframe once shell is fully integrated as shadow dom


    useEffect(() => {
        if (isAuthenticated) {

            dispatch({
                type: "TENANCY/LOAD-ACCOUNTS"
            });

            dispatch({
                type: "USER/LOAD-CONFIG"
            });
        }
    }, [isAuthenticated, userId, dispatch]);

    useEffect(() => {
        if (!loadedUnderBXP && isAuthenticated && !_.isEmpty(configs)) {
            dispatch({
                type: "FEATURES/INITIALIZE",
                payload: configs
            })
        }

    }, [configs, isAuthenticated, dispatch])

    const isFirstTimeUser = !selectedAccountId || !jobDescription;
    const isAccountAndUserLoaded = accountStatus === "SUCCESS" && isJobDescriptionLoaded;

    if(loadedUnderBXP){
        return <QueryClientProvider client={queryClient}>
            <div css={baseCss}>
                <div css={miniContainerCss}>
                  <MiniMainPane {...props}/>
                </div>
            </div>
        </QueryClientProvider>
    }

    if (!isAuthenticated) {
        return <MainLoader/>;
    }

    return <QueryClientProvider client={queryClient}>
        <div css={baseCss}>
            <MainHeader isFirstTimeUser={isFirstTimeUser}/>
            <div css={containerCss}>
                {!isAccountAndUserLoaded && <MainLoader/>}
                {isAccountAndUserLoaded && <>
                    {isFirstTimeUser && <Suspense><FirstTimeUserPane/></Suspense>}
                    {!isFirstTimeUser && <MainPane/>}
                </>}
            </div>
        </div>
    </QueryClientProvider>
}
const App = React.memo((props?: any) => {
    const appState = useSelector((state: RootState) => state.app.appState);
    removeMainLoader(false);
    return (
        <>
            {appState === 'loading' && <MainLoader/>}
            <AppNotifications/>
            {appState === 'ready' && <MainApp {...props}/>}
        </>
    );
});

export default App;
